import BigNumber from 'bignumber.js'
import { Contract, ethers } from 'ethers'
import { encodeFunctionData } from 'viem'
import type { Web3Provider } from '@ethersproject/providers'

import { UserRejectedRequestError, WrongChainError } from 'utils/errors'

import { configSwap, defaultWorldchainConfig } from './configs'

import { getFeeDirect, getFeeWithAmountIn } from './fee'
import { didUserReject, swapErrorToUserReadableMessage } from 'utils/swapErrorToUserReadableMessage'
import { TradeFillType } from 'state/routing/types'
import { abi } from './abi'
import { SmartRouterTrade } from 'lib/SwapRouter'
import { Percent } from '@uniswap/sdk-core'
import { PermitSignature } from './signPermit2'

const CONTRACT_ADDRESS = configSwap.CONTRACT
const feeReceiver = configSwap.feeReceiver
interface SwapOptions {
  slippageTolerance: Percent
  permit?: PermitSignature
  // feeOptions?: FeeOptions
  // flatFeeOptions?: FlatFeeOptions
}
type ParamSwap = {
  trade?: SmartRouterTrade & { holdstation: any }
  options: SwapOptions
  account?: string
  chainId?: number
  getDeadline: any
  provider?: Web3Provider
}

export async function SwapRouterHold(
  callbackTrace: any,
  { account, chainId, provider, options, getDeadline, trade }: ParamSwap
) {
  if (!account) throw new Error('missing account')
  if (!chainId) throw new Error('missing chainId')
  if (!provider) throw new Error('missing provider')
  if (!trade) throw new Error('missing trade')
  const connectedChainId = await provider.getSigner().getChainId()
  if (chainId !== connectedChainId) throw new WrongChainError()
  const tokenOut = trade.outputAmount.currency
  const tokenIn = trade.inputAmount.currency

  callbackTrace.setData('slippageTolerance', options.slippageTolerance.toFixed(2))

  if (tokenIn.isNative) {
    return await submitSwapETHForTokens(callbackTrace, { account, chainId, provider, options, getDeadline, trade })
  }

  if (tokenOut.isNative) {
    return await submitSwapTokensForETH(callbackTrace, { account, chainId, provider, options, getDeadline, trade })
  }

  return await submitSwapTokensForTokens(callbackTrace, { account, chainId, provider, options, getDeadline, trade })

  throw new Error('test')
}

async function submitSwapETHForTokens(fncs: any, { provider, getDeadline, trade }: any) {
  try {
    const deadline = await getDeadline()
    const holdstation = trade.holdstation
    const { data, to } = holdstation
    const tokenOut = holdstation.addons.tokenOut
    const amountIn = holdstation.addons.sellRawAmount
    const fromToken = trade.inputAmount.currency
    const amountInWei = `0x${new BigNumber(amountIn).multipliedBy(Math.pow(10, fromToken.decimals)).toString(16)}`
    const feeAmount =
      getFeeWithAmountIn(tokenOut, new BigNumber(amountInWei), Number(configSwap.FEE_PERCENT))?.feeAmount ?? '0'

    const partnerData = packPartnerData('0', feeAmount)
    const signer = await provider.getSigner()

    const contractAddress = CONTRACT_ADDRESS

    const contract = new Contract(contractAddress, abi, signer)

    const tx = await contract.fillQuoteEthToToken(tokenOut, to, data, partnerData, feeReceiver, {
      value: amountInWei,
    })

    await tx.wait()
    return { type: TradeFillType.Classic as const, response: tx, deadline }
  } catch (error) {
    if (didUserReject(error)) {
      fncs.setStatus('cancelled')
      throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
    } else {
      throw error
    }
  }
}

async function submitSwapTokensForETH(
  fncs: any,
  { account, provider, getDeadline, trade, options }: Required<ParamSwap>
) {
  try {
    const { permit } = options
    const deadline: string = await getDeadline()
    const { holdstation, inputAmount } = trade
    const { data, to } = holdstation
    const tokenIn = holdstation.addons.tokenIn
    const tokenOut = holdstation.addons.tokenOut

    const amountIn = holdstation.addons.sellRawAmount
    const fromToken = inputAmount.currency
    const sellAmount = new BigNumber(amountIn).multipliedBy(Math.pow(10, fromToken.decimals)).toFixed()
    const feeAmount =
      getFeeWithAmountIn(tokenOut, new BigNumber(sellAmount), Number(configSwap.FEE_PERCENT))?.feeAmount ?? '0'
    const signer = await provider.getSigner()

    const partnerData = packPartnerData('0', feeAmount)

    const payload = encodeFunctionData({
      abi,
      functionName: 'fillQuoteTokenToEth',
      args: [
        tokenIn,
        to,
        data,
        sellAmount,
        partnerData,
        feeReceiver,
        {
          nonce: permit!.details.nonce,
          deadline: permit!.sigDeadline,
          signature: permit!.signature,
        },
      ],
    })

    const tx = await signer.sendTransaction({
      from: account,
      to: CONTRACT_ADDRESS,
      data: payload,
    })

    // await tx.wait()
    return { type: TradeFillType.Classic as const, response: tx, deadline }
  } catch (error) {
    if (didUserReject(error)) {
      fncs.setStatus('cancelled')
      throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
    } else {
      throw error
    }
  }
}

async function submitSwapTokensForTokens(
  fncs: any,
  { account, chainId, provider, options, getDeadline, trade }: Required<ParamSwap>
) {
  try {
    const { permit } = options
    const deadline: string = await getDeadline()
    const { holdstation, inputAmount } = trade
    const { data, to } = holdstation
    const tokenIn = holdstation.addons.tokenIn
    const tokenOut = holdstation.addons.tokenOut
    console.log('holdstation', { holdstation })
    const amountIn = holdstation.addons.sellRawAmount
    const feeAmountOut = holdstation.addons.feeAmountOut
    const fromToken = inputAmount.currency
    const sellAmount = new BigNumber(amountIn).multipliedBy(Math.pow(10, fromToken.decimals)).toFixed()
    // const feeAmount =
    //   getFeeWithAmountIn(tokenOut, new BigNumber(sellAmount), Number(configSwap.FEE_PERCENT))?.feeAmount ?? '0'
    const signer = await provider.getSigner()

    let feeToken = '0'
    let feeAmount = '0'
    if (
      getFeeDirect(
        tokenIn,
        tokenOut,
        defaultWorldchainConfig.stableCoins,
        defaultWorldchainConfig.tokens.wld.address
      ) === 0
    ) {
      feeAmount =
        getFeeWithAmountIn(tokenOut, new BigNumber(sellAmount), Number(configSwap.FEE_PERCENT))?.feeAmount ?? '0'
      feeToken = '0'
    } else {
      feeAmount = feeAmountOut ?? '0'
      feeToken = '1'
    }

    const partnerData = packPartnerData('0', feeAmount)

    const payload = encodeFunctionData({
      abi,
      functionName: 'fillQuoteTokenToToken',
      args: [
        tokenIn,
        tokenOut,
        to,
        data,
        sellAmount,
        feeToken,
        partnerData,
        feeReceiver,
        {
          nonce: permit!.details.nonce,
          deadline: permit!.sigDeadline,
          signature: permit!.signature,
        },
      ],
    })

    const tx = await signer.sendTransaction({
      from: account,
      to: CONTRACT_ADDRESS,
      data: payload,
    })

    // await tx.wait()
    return { type: TradeFillType.Classic as const, response: tx, deadline }
  } catch (error) {
    if (didUserReject(error)) {
      fncs.setStatus('cancelled')
      throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error))
    } else {
      throw error
    }
  }
}

function packPartnerData(partnerCode: string, fee: string): string {
  // Convert to JavaScript BigInts for bitwise operation
  const partnerCodeBigInt = BigInt(partnerCode)
  const feeBigInt = BigInt(fee)

  // Shift partner code left by 240 bits
  const shiftedPartnerCode = partnerCodeBigInt << 240n

  // Perform bitwise OR
  const result = shiftedPartnerCode | feeBigInt

  // Convert back to BigNumber
  return new BigNumber(result.toString()).toFixed()
}
